import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldAlert, Clock, Mail, Send } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import api, { apiErr } from "@/lib/api";

export default function Suspended() {
  let info = {};
  try { info = JSON.parse(localStorage.getItem("ban_info") || "{}"); } catch { info = {}; }
  const [msg, setMsg] = useState("");
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const typeLabel = { temporary: "Temporarily suspended", permanent: "Permanently banned", site_wide: "Site-wide ban" }[info.type] || "Account suspended";
  const expires = info.expires_at ? new Date(info.expires_at).toLocaleString() : null;

  const submitAppeal = async () => {
    if (!msg.trim()) { toast.error("Please explain your appeal"); return; }
    setBusy(true);
    try {
      await api.post("/appeals", { ban_id: info.ban_id, message: msg, contact_email: email });
      setSent(true); toast.success("Appeal submitted");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-black grid-bg flex items-center justify-center px-6 py-10">
      <div className="w-full max-w-lg">
        <Link to="/" className="flex items-center justify-center gap-2 font-head font-black text-2xl mb-8">
          <BrandLogo className="w-8 h-8" /> crank<span className="text-volt">.live</span>
        </Link>
        <div data-testid="suspended-card" className="bg-zinc-900 border border-red-500/30 rounded-2xl p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-5">
            <ShieldAlert className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="font-head text-2xl font-bold text-red-400" data-testid="suspended-title">{typeLabel}</h1>
          <p className="text-zinc-400 mt-3" data-testid="suspended-reason">
            {info.reason || "Your account access has been restricted by the platform administrators."}
          </p>
          {expires && (
            <div className="mt-4 inline-flex items-center gap-2 text-sm bg-zinc-800 rounded-full px-4 py-1.5 text-zinc-300">
              <Clock className="w-4 h-4 text-volt" /> Access restored on {expires}
            </div>
          )}
          {info.appeal_enabled && info.ban_id && (
            <div className="mt-6 border-t border-zinc-800 pt-6 text-left">
              {sent ? (
                <p data-testid="appeal-sent" className="text-sm text-volt flex items-center justify-center gap-2 text-center">
                  <Mail className="w-4 h-4" /> Your appeal has been submitted and is under review.
                </p>
              ) : (
                <>
                  <p className="text-sm text-zinc-300 mb-2 flex items-center gap-2"><Mail className="w-4 h-4 text-volt" /> Believe this is a mistake? Submit an appeal:</p>
                  <input data-testid="appeal-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Contact email (optional)" className="w-full bg-black border border-zinc-800 rounded-md px-3 py-2 text-sm outline-none focus:border-volt mb-2" />
                  <textarea data-testid="appeal-message" value={msg} onChange={(e) => setMsg(e.target.value)} rows={3} placeholder="Explain why this decision should be reviewed…" className="w-full bg-black border border-zinc-800 rounded-md px-3 py-2 text-sm outline-none focus:border-volt" />
                  <button data-testid="appeal-submit" onClick={submitAppeal} disabled={busy} className="mt-3 w-full bg-volt text-volt-ink font-semibold rounded-md px-5 py-2 hover:bg-volt-hover transition-colors flex items-center justify-center gap-2 disabled:opacity-50">
                    <Send className="w-4 h-4" /> {busy ? "Submitting…" : "Submit appeal"}
                  </button>
                </>
              )}
            </div>
          )}
          <Link to="/" data-testid="suspended-home" className="inline-block mt-6 text-sm text-zinc-400 hover:text-white">
            ← Return to homepage
          </Link>
          <p className="text-xs text-zinc-600 mt-4">
            Changing your Discord username will not restore access — enforcement uses your permanent Discord account.
          </p>
        </div>
      </div>
    </div>
  );
}
